'use client';

import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cva, type VariantProps } from 'class-variance-authority';

import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { cn } from '@/utils/cn-tw-merge';

interface SheetProps {
  open?: boolean;
  children: React.ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
}

const Sheet = ({ open, children, onOpenChange }: SheetProps) => {
  return (
    <SheetPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <VisuallyHidden>
        <SheetTitle>Menu lateral</SheetTitle>
      </VisuallyHidden>

      {children}
    </SheetPrimitive.Root>
  );
};

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-gray-600/40 md:backdrop-blur-[4px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      'data-[state=closed]:fill-mode-forwards',
      'data-[state=open]:duration-500 data-[state=closed]:duration-300',
      className,
    )}
    data-testid="sheet-overlay--sheet-component"
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  [
    'fixed z-50',
    'gap-4 p-5',
    'bg-white dark:bg-slate-950',
    'shadow-lg',
    'transition ease-in-out',
    'data-[state=open]:animate-in data-[state=open]:duration-500',
    'data-[state=closed]:animate-out data-[state=closed]:duration-300',
    'data-[state=closed]:fill-mode-forwards',
  ],
  {
    variants: {
      side: {
        top: [
          'inset-x-0 top-0',
          'border-b',
          'data-[state=closed]:slide-out-to-top',
          'data-[state=open]:slide-in-from-top',
        ],

        bottom: [
          'inset-x-0 bottom-0',
          'border-t',
          'data-[state=closed]:slide-out-to-bottom',
          'data-[state=open]:slide-in-from-bottom',
        ],

        left: [
          'inset-y-0 left-0',
          'h-full w-3/4',
          'border-r',
          'data-[state=closed]:slide-out-to-left',
          'data-[state=open]:slide-in-from-left',
        ],

        right: [
          'inset-y-0 right-0',
          'h-full w-3/4',
          'border-l',
          'data-[state=closed]:slide-out-to-right',
          'data-[state=open]:slide-in-from-right',
        ],

        center: [
          'inset-0 m-auto',
          'w-auto h-full sm:h-fit',
          'border sm:rounded-2xl',
          'overflow-hidden',
          'sm:w-[90vw] sm:max-w-[22.5rem] sm:max-h-[67vh]',
          'data-[state=closed]:slide-out-to-bottom',
          'data-[state=open]:slide-in-from-bottom',
          'sm:data-[state=closed]:fade-out-0 sm:data-[state=open]:fade-in-0',
          'sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0',
          'sm:!duration-200',
        ],

        ['center-v2']: [
          'inset-0 m-auto',
          'w-auto h-full md:h-fit',
          'border md:rounded-2xl',
          'overflow-hidden',
          'md:w-[90vw] md:max-w-[22.5rem] md:max-h-[67vh]',
          'data-[state=closed]:slide-out-to-bottom',
          'data-[state=open]:slide-in-from-bottom',
          'md:data-[state=closed]:fade-out-0 md:data-[state=open]:fade-in-0',
          'md:data-[state=closed]:slide-out-to-bottom-0 md:data-[state=open]:slide-in-from-bottom-0',
          'md:!duration-200',
        ],

        centerLarge: [
          'inset-0 m-auto',
          'w-auto h-full lg:h-fit',
          'border lg:rounded-2xl',
          'overflow-hidden',
          'lg:w-[90vw] lg:max-w-[50rem] lg:max-h-[67vh]',
          'data-[state=closed]:slide-out-to-bottom',
          'data-[state=open]:slide-in-from-bottom',
          'lg:data-[state=closed]:fade-out-0 lg:data-[state=open]:fade-in-0',
          'lg:data-[state=closed]:slide-out-to-bottom-0 lg:data-[state=open]:slide-in-from-bottom-0',
          'lg:!duration-200',
        ],

        centerFit: [
          'inset-x-0 bottom-0',
          'border-t',
          'data-[state=closed]:slide-out-to-bottom',
          'data-[state=open]:slide-in-from-bottom',
          'sm:inset-0 sm:m-auto',
          'sm:w-auto sm:h-full sm:h-fit',
          'sm:border sm:rounded-2xl',
          'overflow-hidden',
          'sm:w-[90vw] sm:max-w-[24rem] sm:max-h-[67vh]',
          'sm:data-[state=closed]:fade-out-0 sm:data-[state=open]:fade-in-0',
          'sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0',
          'sm:data-[state=closed]:slide-out-to-bottom sm:data-[state=open]:slide-in-from-bottom',
          'sm:!duration-200',
        ],

        centerWide: [
          'inset-x-0 bottom-0',
          'border-t',
          'data-[state=closed]:slide-out-to-bottom',
          'data-[state=open]:slide-in-from-bottom',
          'sm:inset-0 sm:m-auto',
          'sm:w-auto sm:h-full sm:h-fit',
          'sm:border sm:rounded-2xl',
          'overflow-hidden',
          'sm:w-[90vw] sm:max-w-[40rem] sm:max-h-[67vh]',
          'sm:data-[state=closed]:fade-out-0 sm:data-[state=open]:fade-in-0',
          'sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0',
          'sm:data-[state=closed]:slide-out-to-bottom sm:data-[state=open]:slide-in-from-bottom',
          'sm:!duration-200',
        ],
      },
    },
    defaultVariants: {
      side: 'right',
    },
  },
);

type SheetVariants = VariantProps<typeof sheetVariants>;

export interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
  SheetVariants {
  closingTagPosition?: 'left' | 'right';
  overlayClassName?: string;
  hasHeader?: boolean;
  title?: string;
  description?: string;
}

const overlayDurationBySide: Record<NonNullable<SheetVariants['side']>, string> = {
  top: '',
  bottom: '',
  left: '',
  right: '',
  center: 'sm:!duration-200',
  ['center-v2']: 'md:!duration-200',
  centerFit: 'sm:!duration-200',
  centerWide: 'sm:!duration-200',
  centerLarge: 'lg:!duration-200',
};

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  (
    {
      side = 'right',
      closingTagPosition = 'right',
      className,
      children,
      overlayClassName,
      hasHeader = false,
      title,
      description,
      ...props
    },
    ref,
  ) => (
    <SheetPortal>
      <SheetOverlay className={cn(overlayDurationBySide[side ?? 'right'], overlayClassName)} />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          sheetVariants({ side } as SheetVariants),
          'flex flex-col max-h-[100dvh]',
          className,
        )}
        {...props}
      >
        <SheetPrimitive.Title className="sr-only" suppressHydrationWarning>
          {title || 'Conteúdo da modal'}
        </SheetPrimitive.Title>

        <SheetPrimitive.Description className="sr-only" suppressHydrationWarning>
          {description || ''}
        </SheetPrimitive.Description>

        <div className="flex flex-col h-full min-h-0">
          {children}
        </div>
        {!hasHeader && (
          <SheetPrimitive.Close asChild>
            <ModalCloseButton
              className={`absolute ${closingTagPosition === 'right' ? 'right-4' : 'left-4'} top-4`}
            />
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  ),
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-2 text-left', className)} {...props} />
  ),
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex', className)} {...props} />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    asChild
  >
    <div
      role="heading"
      aria-level={1}
      className={cn('text-lg font-semibold text-slate-950 dark:text-slate-50', className)}
      suppressHydrationWarning
      {...props}
    />
  </SheetPrimitive.Title>
));

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('text-sm text-slate-500 dark:text-slate-400 ', className)}
    suppressHydrationWarning
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
